import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import PageLoader from '../../components/PageLoader/PageLoader';
import Layout from '../../components/Layout/Layout';
import ErrorBoundary from '../../components/Error/ErrorBoundary';
import Sider from './components/Sider/Sider';
import OrganisationPlan from './scenes/OrganisationPlan/OrganisationPlan';
import Broker from './scenes/Broker/Broker';
import BrokerageSubscriptions from './scenes/Subscription/scenes/BrokerageSubscriptions/BrokerageSubscriptions';
import MySubscriptions from './scenes/Subscription/scenes/MySubscriptions/MySubscriptions';
import Transaction from './scenes/Transaction/Transaction';
import User from './scenes/User/User';
import { logout } from '../../components/Login/LoginAction';
import { fetchBroker, fetchBrokers, fetchBrokerage } from './scenes/Broker/BrokerActions';
import {
  brokerInfoSelector,
  brokerLookupCompleteSelector,
  loadingBrokerSelector,
} from './scenes/Broker/BrokerSelectors';
import UnderConstructionPanel from '../../components/UnderConstruction/UnderConstructionPanel';

const Admin = ({
  match,
  location,
  brokerInfo,
  brokerLookupComplete,
  loadingBroker,
  fetchBroker,
  fetchBrokers,
  fetchBrokerage,
  logout,
}) => {
  useEffect(() => {
    if (brokerLookupComplete || loadingBroker) return;
    fetchBroker();
  }, [brokerLookupComplete, loadingBroker, fetchBroker]);

  useEffect(() => {
    const brokerageId = brokerInfo?.brokerageId;
    if (!brokerageId) return;

    fetchBrokers(brokerageId);
    fetchBrokerage(brokerageId);
  }, [brokerInfo, fetchBrokers, fetchBrokerage]);

  const brokerMenuItems = [
    {
      route: 'brokerage-subscriptions',
      name: 'Subscription Management',
      type: 'menu-item',
      component: <BrokerageSubscriptions />,
      icon: 'key',
    },
    {
      route: 'organisations',
      name: 'Organisations & Plans',
      type: 'menu-item',
      component: <OrganisationPlan />,
      icon: 'unordered-list',
    },
    {
      route: 'admin-users',
      name: 'My Admin Users',
      type: 'menu-item',
      component: <Broker />,
      icon: 'team',
    },
    {
      route: 'my-subscriptions',
      name: 'My Subscriptions',
      type: 'menu-item',
      component: <MySubscriptions />,
      icon: 'profile',
    },
    {
      route: 'transactions',
      name: 'Transaction History',
      type: 'menu-item',
      component: <Transaction />,
      icon: 'dollar',
    },
    {
      route: 'settings',
      name: 'Settings',
      type: 'menu-item',
      component: <User logoWidth={120} />,
      icon: 'setting',
    },
  ];

  const adminMenuItems = [
    {
      route: 'organisations',
      name: 'Organisations & Plans',
      type: 'menu-item',
      component: <OrganisationPlan />,
      icon: 'unordered-list',
    },
    {
      route: 'my-subscriptions',
      name: 'My Subscriptions',
      type: 'menu-item',
      component: <MySubscriptions />,
      icon: 'key',
    },
    {
      route: 'transactions',
      name: 'Transaction History',
      type: 'menu-item',
      component: <Transaction />,
      icon: 'dollar',
    },
    {
      route: 'settings',
      name: 'Settings',
      type: 'menu-item',
      component: <User logoWidth={120} />,
      icon: 'setting',
    },
  ];

  const menuItems = brokerInfo ? brokerMenuItems : adminMenuItems;
  const defaultRoute = brokerInfo ? 'brokerage-subscriptions' : menuItems[0].route;

  const adminPath = location.pathname.startsWith(match.url)
    ? location.pathname.slice(match.url.length).replace(/^\//, '')
    : '';
  const selectedKey = adminPath.split('/')[0] || defaultRoute;

  // Full navigation so no in-memory session state (axios headers, Redux) survives sign-out.
  const onLogout = async () => {
    await logout();
    window.location.assign('/');
  };

  return (
    <Layout
      sider={
        <Sider
          menuItems={menuItems}
          selectedKey={selectedKey}
          basePath={match.url}
          onLogout={onLogout}
        />
      }
    >
      {loadingBroker ? (
        <PageLoader />
      ) : (
        <UnderConstructionPanel active={false} moduleName="Admin">
          <Switch>
            <Route exact path={match.url} render={() => <Redirect to={`${match.url}/${defaultRoute}`} />} />
            {menuItems.map((item) => (
              <Route
                exact
                key={item.route}
                path={`${match.url}/${item.route}`}
                render={() => <ErrorBoundary>{item.component}</ErrorBoundary>}
              />
            ))}
            <Redirect to={`${match.url}/${defaultRoute}`} />
          </Switch>
        </UnderConstructionPanel>
      )}
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  brokerInfo: brokerInfoSelector(state),
  brokerLookupComplete: brokerLookupCompleteSelector(state),
  loadingBroker: loadingBrokerSelector(state),
});

const mapDispatchToProps = {
  fetchBroker,
  fetchBrokers,
  fetchBrokerage,
  logout,
};

export default connect(mapStateToProps, mapDispatchToProps)(Admin);
